import React from "react";
import {
  Stack,
  Group,
  Select,
  NumberInput,
  Checkbox,
  Switch,
  Tooltip,
  Text,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";

export interface StaffServiceSettingsData {
  id?: string | null;
  enabled: boolean;
  isOpen: boolean;
  price: number;
  duration: string;
  processingTime?: string;
  finishingTime?: string;
  bufferTime?: string;
  startsAt: boolean;
  is_self_servicable: boolean;
}

interface StaffServiceSettingsFieldsProps {
  settings: StaffServiceSettingsData;
  onChange: (settings: StaffServiceSettingsData) => void;
  isMobile?: boolean;
}

function generateDurationOptions() {
  const options = [];
  for (let minutes = 5; minutes <= 240; minutes += 5) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label =
      hours > 0
        ? `${hours} hr${hours > 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
        : `${mins} min`;
    options.push({ value: `${minutes}`, label });
  }
  return options;
}

/** A pair of fields side by side on desktop, stacked on mobile. */
function FieldRow({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: React.ReactNode;
}) {
  return isMobile ? (
    <Stack gap="sm">{children}</Stack>
  ) : (
    <Group grow align="flex-end" gap="md">
      {children}
    </Group>
  );
}

/**
 * Reusable form fields for staff service customization.
 * This component renders the Duration, Processing Time, Finishing Time, Buffer Time,
 * Price, Starts At, and Show in Online Booking fields.
 * It should be wrapped in a Collapse by the parent.
 */
export default function StaffServiceSettingsFields({
  settings,
  onChange,
  isMobile = false,
}: StaffServiceSettingsFieldsProps) {
  // Desktop caps each input; on mobile the stacked inputs take the full width.
  const fieldStyle = isMobile ? undefined : { maxWidth: 160 };

  const updateSetting = (key: keyof StaffServiceSettingsData, value: any) => {
    onChange({
      ...settings,
      [key]: value,
    });
  };

  return (
    <Stack gap="sm">
      <FieldRow isMobile={isMobile}>
        <Select
          label={
            <Group gap={4} align="normal">
              <Text span inherit>
                Base Duration
              </Text>
              <Tooltip
                label="Active time spent on service initially"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={settings.duration}
          onChange={(value) => updateSetting("duration", value)}
          data={generateDurationOptions()}
          style={fieldStyle}
        />
        <Select
          label={
            <Group gap={4} align="normal">
              <Text span inherit>
                Processing Time
              </Text>
              <Tooltip
                label="Inactive time for treatments/products to process (e.g., color development)"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={settings.processingTime ?? "0"}
          onChange={(value) => {
            // Convert "None" (value "0") to "0" string for consistency
            updateSetting(
              "processingTime",
              value === "0" || value === null ? "0" : value,
            );
          }}
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          style={fieldStyle}
        />
      </FieldRow>
      <FieldRow isMobile={isMobile}>
        <Select
          label={
            <Group gap={4} align="normal">
              <Text span inherit>
                Finishing Time
              </Text>
              <Tooltip
                label="Active time to finalize the service after processing"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={settings.finishingTime ?? "0"}
          onChange={(value) => {
            // Convert "None" (value "0") to "0" string for consistency
            updateSetting(
              "finishingTime",
              value === "0" || value === null ? "0" : value,
            );
          }}
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          style={fieldStyle}
        />
        <Select
          label={
            <Group gap={4} align="normal">
              <Text span inherit>
                Buffer Time
              </Text>
              <Tooltip
                label="Time blocked before next appointment begins (ex: cleanup, breaks)"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={settings.bufferTime ?? "0"}
          onChange={(value) => {
            // Convert "None" (value "0") to "0" string for consistency
            updateSetting(
              "bufferTime",
              value === "0" || value === null ? "0" : value,
            );
          }}
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          style={fieldStyle}
        />
      </FieldRow>
      <FieldRow isMobile={isMobile}>
        <NumberInput
          label="Price"
          value={settings.price}
          onChange={(value) => updateSetting("price", Number(value) || 0)}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          style={fieldStyle}
        />
        <Checkbox
          label="Starts At"
          checked={settings.startsAt}
          onChange={(event) =>
            updateSetting("startsAt", event.currentTarget.checked)
          }
          mt={isMobile ? 0 : 25}
        />
      </FieldRow>
      <Switch
        label="Show in online booking"
        checked={settings.is_self_servicable}
        onChange={(event) =>
          updateSetting("is_self_servicable", event.currentTarget.checked)
        }
      />
    </Stack>
  );
}

// Helper to create default settings
export function createDefaultStaffSettings(
  serviceDefaults: {
    price: number;
    duration: number | string;
    showInOnlineBooking: boolean;
    processingTime?: number | string;
    finishingTime?: number | string;
    bufferTime?: number | string;
  },
  existingId?: string | null,
): StaffServiceSettingsData {
  return {
    id: existingId || null,
    enabled: true,
    isOpen: true,
    price: serviceDefaults.price,
    duration: String(serviceDefaults.duration),
    processingTime: String(serviceDefaults.processingTime ?? "0"),
    finishingTime: String(serviceDefaults.finishingTime ?? "0"),
    bufferTime: String(serviceDefaults.bufferTime ?? "0"),
    startsAt: false,
    is_self_servicable: serviceDefaults.showInOnlineBooking,
  };
}

// Re-export the duration options generator for use elsewhere
export { generateDurationOptions };
